import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, downloadFile, list } from '../lib/api'
import { Empty, Money, PageHead, Skel } from '../components/ui'
import { thaiDate, thaiDateTime } from '../lib/format'

interface DSARRequest {
  id: string
  subject_ref: string
  kind: string
  status: string
  due_at: string
  note: string
  resolution: string
  overdue: boolean
  created_at: string
  updated_at: string
}

interface PreviewRow {
  occurred_at?: string
  amount_satang?: number
  text: string
}

interface SubjectTable {
  table: string
  label: string
  match_key: string
  count: number
  preview: PreviewRow[]
  note?: string
  truncated?: boolean
}

interface SubjectData {
  subject_ref_masked: string
  total: number
  tables: SubjectTable[]
  manifest: {
    regulation: string
    statutory_days: number
    tables: { table: string; label: string; match_key: string; deterministic: boolean; note?: string }[]
    not_in_scope: string[]
  }
  generated_at: string
}

interface PseudonymiseResult {
  subject_ref_masked: string
  alias: string
  transactions: number
}

const kindLabel: Record<string, string> = {
  access: 'ขอเข้าถึงข้อมูล',
  erasure: 'ขอลบข้อมูล',
  rectify: 'ขอแก้ไขข้อมูล',
}

const statusLabel: Record<string, string> = {
  received: 'รับคำขอ',
  verifying: 'กำลังตรวจสอบตัวตน',
  fulfilling: 'กำลังดำเนินการ',
  completed: 'เสร็จสิ้น',
  rejected: 'ปฏิเสธ',
}

const nextSteps: Record<string, { status: string; label: string }[]> = {
  received: [{ status: 'verifying', label: 'เริ่มตรวจสอบ' }],
  verifying: [{ status: 'fulfilling', label: 'เริ่มดำเนินการ' }],
  fulfilling: [
    { status: 'completed', label: 'เสร็จสิ้น' },
    { status: 'rejected', label: 'ปฏิเสธ' },
  ],
  rejected: [{ status: 'received', label: 'เปิดใหม่' }],
  completed: [],
}

export function DSAR() {
  const qc = useQueryClient()
  const [subjectRef, setSubjectRef] = useState('')
  const [kind, setKind] = useState('access')
  const [note, setNote] = useState('')
  const [selected, setSelected] = useState<string | null>(null)
  const [dataSubject, setDataSubject] = useState('')
  const [resolution, setResolution] = useState('')
  const [confirmAlias, setConfirmAlias] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const requests = useQuery({
    queryKey: ['dsar'],
    queryFn: () => list<DSARRequest>('/v1/dsar'),
    staleTime: 15_000,
  })

  const subjectData = useQuery({
    queryKey: ['dsar-subject', dataSubject],
    queryFn: () => api<SubjectData>(`/v1/dsar/subject-data?subject_ref=${encodeURIComponent(dataSubject)}`),
    enabled: dataSubject.trim().length >= 3,
  })

  const create = useMutation({
    mutationFn: () => apiWrite<DSARRequest>('/v1/dsar', 'POST', {
      subject_ref: subjectRef.trim(),
      kind,
      note: note.trim(),
    }),
    onSuccess: (row) => {
      setSubjectRef('')
      setNote('')
      setNotice(`เปิดคำขอแล้ว · ครบกำหนด ${thaiDate(row.due_at)}`)
      setSelected(row.id)
      setDataSubject(row.subject_ref)
      setResolution('')
      void qc.invalidateQueries({ queryKey: ['dsar'] })
    },
    onError: (e) => setNotice(e instanceof Error ? e.message : 'เปิดคำขอไม่สำเร็จ'),
  })

  const move = useMutation({
    mutationFn: (input: { id: string; status: string; resolution: string }) =>
      apiWrite<DSARRequest>(`/v1/dsar/${input.id}`, 'PATCH', {
        status: input.status,
        resolution: input.resolution,
      }),
    onSuccess: (row) => {
      setResolution(row.resolution)
      setNotice(`อัปเดตสถานะเป็น ${statusLabel[row.status] ?? row.status}`)
      void qc.invalidateQueries({ queryKey: ['dsar'] })
    },
    onError: (e) => setNotice(e instanceof Error ? e.message : 'เปลี่ยนสถานะไม่สำเร็จ'),
  })

  const pseudonymise = useMutation({
    mutationFn: (ref: string) => apiWrite<PseudonymiseResult>('/v1/dsar/pseudonymise', 'POST', { subject_ref: ref }),
    onSuccess: (out) => {
      setConfirmAlias(false)
      setNotice(`แทนชื่อด้วย ${out.alias} ใน ${out.transactions} ธุรกรรม — ไม่มีการลบแถว`)
      void qc.invalidateQueries({ queryKey: ['dsar'] })
      void qc.invalidateQueries({ queryKey: ['dsar-subject', dataSubject] })
    },
    onError: (e) => setNotice(e instanceof Error ? e.message : 'แทนชื่อไม่สำเร็จ'),
  })

  const rows = requests.data ?? []
  const active = rows.find((row) => row.id === selected) ?? null
  const openCount = rows.filter((row) => row.status !== 'completed' && row.status !== 'rejected').length
  const overdueCount = rows.filter((row) => row.overdue).length

  function select(row: DSARRequest) {
    setSelected(row.id)
    setDataSubject(row.subject_ref)
    setResolution(row.resolution)
    setConfirmAlias(false)
    setNotice(null)
  }

  function advance(row: DSARRequest, status: string) {
    if ((status === 'completed' || status === 'rejected') && resolution.trim().length < 5) {
      setSelected(row.id)
      setDataSubject(row.subject_ref)
      setNotice('ระบุผลการดำเนินการอย่างน้อย 5 ตัวอักษรก่อนปิดคำขอ')
      return
    }
    move.mutate({ id: row.id, status, resolution: resolution.trim() })
  }

  const activeNext = active ? nextSteps[active.status] ?? [] : []

  return (
    <>
      <PageHead
        title="คำขอใช้สิทธิ PDPA"
        sub="ทะเบียนคำขอเข้าถึง ลบ หรือแก้ไขข้อมูลส่วนบุคคล · กำหนดตอบกลับ 30 วัน"
        actions={
          <button className="btn btn-ghost"
            onClick={() => void downloadFile('/v1/dsar/export.csv', 'auditai-dsar.csv')}>
            ส่งออก CSV
          </button>
        }
      />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className="chip">{rows.length} คำขอทั้งหมด</span>
        <span className="chip">{openCount} ยังเปิดอยู่</span>
        <span className={`chip${overdueCount > 0 ? ' gold' : ''}`}>{overdueCount} เกินกำหนด</span>
        <span className="chip">ตอบกลับภายใน 30 วัน</span>
      </div>

      <div className="card card-pad" style={{ marginBottom: 18 }}>
        <div className="card-label">เปิดคำขอใหม่</div>
        <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 14 }}>
          <div className="field" style={{ flex: '2 1 260px', margin: 0 }}>
            <label htmlFor="dsar-subject">อ้างอิงเจ้าของข้อมูล</label>
            <input id="dsar-subject" className="input" maxLength={120}
              placeholder="อีเมล หรือรหัสคู่ค้า เช่น V-1042"
              value={subjectRef} onChange={(e) => setSubjectRef(e.target.value)} />
          </div>
          <div className="field" style={{ flex: '1 1 180px', margin: 0 }}>
            <label htmlFor="dsar-kind">ประเภทคำขอ</label>
            <select id="dsar-kind" className="select" value={kind} onChange={(e) => setKind(e.target.value)}>
              {Object.entries(kindLabel).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ flex: '2 1 220px', margin: 0 }}>
            <label htmlFor="dsar-note">บันทึก</label>
            <input id="dsar-note" className="input" maxLength={400}
              placeholder="ช่องทางที่ได้รับคำขอ (ไม่บังคับ)"
              value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <button className="btn btn-primary"
            disabled={subjectRef.trim().length < 3 || create.isPending}
            onClick={() => create.mutate()}>
            {create.isPending ? 'กำลังเปิด…' : 'เปิดคำขอ'}
          </button>
        </div>
        <div className="dim" style={{ fontSize: 11.5, marginTop: 10, lineHeight: 1.7 }}>
          ระบบคำนวณวันครบกำหนดจากวันที่รับคำขอ และเตือนเมื่อเลยกำหนด · คำขอลบจะไม่ลบแถวบัญชี
          แต่แทนชื่อด้วยรหัสเพื่อรักษาร่องรอยการตรวจสอบ
        </div>
      </div>

      {requests.isLoading ? <div className="stack"><Skel h={52} /><Skel h={52} /><Skel h={52} /></div>
        : rows.length === 0 ? (
          <div className="card"><Empty title="ยังไม่มีคำขอใช้สิทธิ"
            body="เปิดคำขอแรกเมื่อมีเจ้าของข้อมูลขอดู ลบ หรือแก้ไขข้อมูลของตน" /></div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>เจ้าของข้อมูล</th><th>ประเภท</th><th>สถานะ</th>
                  <th>กำหนดตอบกลับ</th><th>สร้างเมื่อ</th><th>การดำเนินการ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} onClick={() => select(row)}
                    style={{ cursor: 'pointer' }}>
                    <td className="mono">{row.subject_ref}</td>
                    <td>{kindLabel[row.kind] ?? row.kind}</td>
                    <td>
                      <span className={`chip${row.status === 'completed' ? ' gold' : ''}`}>
                        {statusLabel[row.status] ?? row.status}
                      </span>
                    </td>
                    <td>
                      <span className="row" style={{ gap: 6 }}>
                        <span className="tnum">{thaiDate(row.due_at)}</span>
                        {row.overdue ? (
                          <span className="chip" style={{ color: 'var(--color-danger, #c97a7a)' }}>เกินกำหนด</span>
                        ) : null}
                      </span>
                    </td>
                    <td className="dim">{thaiDate(row.created_at)}</td>
                    <td>
                      <span className="row" style={{ gap: 6, flexWrap: 'wrap' }}
                        onClick={(e) => e.stopPropagation()}>
                        {(nextSteps[row.status] ?? []).map((step) => (
                          <button key={step.status} className="btn btn-ghost btn-sm"
                            disabled={move.isPending}
                            onClick={() => advance(row, step.status)}>
                            {step.label}
                          </button>
                        ))}
                        <button className="btn btn-ghost btn-sm" onClick={() => select(row)}>ดูข้อมูล</button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {notice ? <div className="dim" style={{ marginTop: 12, fontSize: 12 }}>{notice}</div> : null}

      {active ? (
        <div className="grid-main" style={{ marginTop: 22 }}>
          <div className="card card-pad">
            <div className="row">
              <div className="card-label">คำขอที่เลือก</div>
              <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
                onClick={() => setSelected(null)}>ปิด</button>
            </div>
            <div className="heading-m" style={{ marginTop: 10 }}>{active.subject_ref}</div>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
              <span className="chip">{kindLabel[active.kind] ?? active.kind}</span>
              <span className={`chip${active.status === 'completed' ? ' gold' : ''}`}>
                {statusLabel[active.status] ?? active.status}
              </span>
              {active.overdue ? (
                <span className="chip" style={{ color: 'var(--color-danger, #c97a7a)' }}>เกินกำหนด</span>
              ) : null}
            </div>
            <div className="dim" style={{ fontSize: 12, marginTop: 12, lineHeight: 1.8 }}>
              รับเมื่อ {thaiDateTime(active.created_at)} · ครบกำหนด {thaiDate(active.due_at)}
              {active.note ? <> · บันทึก: {active.note}</> : null}
            </div>

            {activeNext.length > 0 ? (
              <div style={{ marginTop: 16 }}>
                <div className="field">
                  <label htmlFor="dsar-resolution">ผลการดำเนินการ</label>
                  <input id="dsar-resolution" className="input" maxLength={500}
                    placeholder="เช่น ส่งออกข้อมูลให้เจ้าของข้อมูลทางอีเมลแล้ว"
                    value={resolution} onChange={(e) => setResolution(e.target.value)} />
                </div>
                <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  {activeNext.map((step) => (
                    <button key={step.status} className="btn btn-sm" disabled={move.isPending}
                      style={step.status === 'rejected' ? { color: 'var(--color-danger, #c97a7a)' } : undefined}
                      onClick={() => advance(active, step.status)}>
                      {step.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="dim" style={{ fontSize: 12, marginTop: 14 }}>
                คำขอนี้ปิดแล้ว{active.resolution ? ` · ${active.resolution}` : ''}
              </div>
            )}

            <div className="rule" style={{ margin: '20px 0 16px' }} />
            <div className="eyebrow">การปกปิดตัวตน</div>
            <p className="dim" style={{ fontSize: 11.5, lineHeight: 1.8, marginTop: 8 }}>
              แทนชื่อคู่ค้าด้วยรหัสที่คำนวณจากเจ้าของข้อมูล · แถวบัญชีและร่องรอยการตรวจสอบยังอยู่ครบ
            </p>
            {confirmAlias ? (
              <div className="row" style={{ gap: 8, marginTop: 10 }}>
                <button className="btn btn-sm" disabled={pseudonymise.isPending}
                  onClick={() => pseudonymise.mutate(active.subject_ref)}>
                  {pseudonymise.isPending ? 'กำลังแทนชื่อ…' : 'ยืนยันแทนชื่อ'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirmAlias(false)}>ยกเลิก</button>
              </div>
            ) : (
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 10 }}
                onClick={() => setConfirmAlias(true)}>แทนชื่อด้วยรหัส</button>
            )}
          </div>

          <div className="card card-pad">
            <div className="row">
              <div className="card-label">ดูข้อมูลของเจ้าของข้อมูล</div>
              {subjectData.data ? (
                <span className="chip" style={{ marginLeft: 'auto' }}>{subjectData.data.total} รายการ</span>
              ) : null}
            </div>
            <div className="dim mono" style={{ fontSize: 12, marginTop: 10 }}>
              {subjectData.data?.subject_ref_masked ?? active.subject_ref}
            </div>

            {subjectData.isLoading ? <div style={{ marginTop: 14 }}><Skel h={90} /></div>
              : subjectData.isError ? (
                <div className="form-error" style={{ marginTop: 14 }}>
                  {subjectData.error instanceof Error ? subjectData.error.message : 'โหลดข้อมูลไม่สำเร็จ'}
                </div>
              ) : subjectData.data ? (
                <>
                  <div style={{ marginTop: 12 }}>
                    {subjectData.data.tables.map((table) => (
                      <div key={table.table} style={{ marginTop: 14 }}>
                        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 12.5 }}>{table.label}</span>
                          <span className={`chip${table.count > 0 ? ' gold' : ''}`}>{table.count} รายการ</span>
                          <span className="dim mono" style={{ fontSize: 10.5, marginLeft: 'auto' }}>
                            {table.table}
                          </span>
                        </div>
                        {table.preview.length > 0 ? (
                          <div style={{ marginTop: 8 }}>
                            {table.preview.map((line, i) => (
                              <div key={`${table.table}-${i}`} className="evidence-row"
                                style={{ gridTemplateColumns: '104px minmax(0,1fr) auto' }}>
                                <span className="dim tnum" style={{ fontSize: 11 }}>
                                  {line.occurred_at ? thaiDate(line.occurred_at) : '—'}
                                </span>
                                <span style={{ fontSize: 12, wordBreak: 'break-word' }}>{line.text}</span>
                                <span className="tnum" style={{ fontSize: 11.5 }}>
                                  {line.amount_satang ? <Money satang={line.amount_satang} /> : null}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>
                            {table.note ?? 'ไม่พบข้อมูลที่จับคู่ได้'}
                          </div>
                        )}
                        {table.preview.length > 0 && table.truncated ? (
                          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                            แสดงตัวอย่างบางส่วนจากทั้งหมด {table.count} รายการ
                          </div>
                        ) : null}
                        {table.preview.length > 0 && table.note ? (
                          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>{table.note}</div>
                        ) : null}
                      </div>
                    ))}
                  </div>

                  <div className="rule" style={{ margin: '18px 0 12px' }} />
                  <div className="dim" style={{ fontSize: 10.5, lineHeight: 1.8 }}>
                    ขอบเขต: {subjectData.data.manifest.tables.map((t) => t.table).join(' · ')}
                    <br />
                    ไม่รวม: {subjectData.data.manifest.not_in_scope.join(' · ')}
                    <br />
                    ข้อมูลตัวอย่างถูกปิดบางส่วน (อีเมล เบอร์โทร เลขบัตรประชาชน) ก่อนแสดงบนหน้าจอ
                  </div>
                </>
              ) : (
                <div className="dim" style={{ fontSize: 12, marginTop: 14 }}>
                  เลือกคำขอจากตารางเพื่อดูข้อมูลที่จับคู่ได้
                </div>
              )}
          </div>
        </div>
      ) : null}
    </>
  )
}
